import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from './part-viewer.style.js';

import { updatePart, getPartsByCompany } from '@cmms/api';
import { db } from '../../services/auth.service.js';

@customElement('part-viewer')
export class PartViewer extends LitElement {
  @property({ type: Boolean })
  open = false;

  @property({ type: Object })
  part: any = null;

  @state()
  private isEditing = false;

  @state()
  private isLoading = false;

  @state()
  private errorMessage = '';

  @state()
  private originalPart: any = null;

  static styles = styles;

  private normalizePartName(name: string): string {
    return name
      .trim() // Remove leading/trailing spaces
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .toLowerCase(); // Convert to lowercase
  }

  private async checkDuplicatePartName(name: string): Promise<{ isDuplicate: boolean; existingName?: string }> {
    try {
      // Normalize the input name for comparison
      const normalizedInputName = this.normalizePartName(name);
      
      // Use the existing API function to get parts by company
      const result = await getPartsByCompany(db, this.part.company_id);
      
      if (result.error) {
        console.error('Error fetching parts for duplicate check:', result.error);
        return { isDuplicate: false }; // Allow submission if we can't check
      }
      
      if (result.data && result.data.length > 0) {
        // Check if any existing part has the same normalized name (excluding current part)
        for (const part of result.data) {
          if (part.id !== this.part.id) { // Exclude current part from duplicate check
            const existingName = part.model?.name || part.name;
            if (existingName) {
              const normalizedExistingName = this.normalizePartName(existingName);
              if (normalizedExistingName === normalizedInputName) {
                return { isDuplicate: true, existingName: existingName };
              }
            }
          }
        }
      }
      
      return { isDuplicate: false }; // No existing parts, so no duplicates
    } catch (error) {
      console.error('Error checking for duplicate part name:', error);
      // If we can't check for duplicates, allow the submission to proceed
      // The server-side validation will catch any issues
      return { isDuplicate: false };
    }
  }

  private handleFieldUpdate(field: string, value: any) {
    if (!this.isEditing) return;

    // Handle numeric fields
    if (['stock_qty', 'min_qty', 'max_qty', 'reorder_level', 'cost'].includes(field)) {
      value = value === '' ? 0 : parseFloat(value);
    }

    this.part = {
      ...this.part,
      [field]: value
    };
  }

  private async handleSave() {
    if (!this.isEditing) return;

    // Check for duplicate part name if name was changed
    if (this.part.name !== this.originalPart.name) {
      const duplicateCheck = await this.checkDuplicatePartName(this.part.name);
      if (duplicateCheck.isDuplicate) {
        const normalizedExistingName = this.normalizePartName(duplicateCheck.existingName!);
        this.errorMessage = `A part with the name '${normalizedExistingName}' already exists`;
        return;
      }
    }

    try {
      this.isLoading = true;
      this.errorMessage = '';

      // Normalize the name before saving
      const normalizedName = this.normalizePartName(this.part.name);

      const updateData = {
        ...this.part,
        name: normalizedName,
        updated_at: Date.now()
      };

      const result = await updatePart(db, this.part.id, updateData);

      if (!result.error) {
        this.originalPart = { ...this.part };
        this.isEditing = false;
        this.errorMessage = '';
        
        // Dispatch event with the updated part
        this.dispatchEvent(new CustomEvent('part-updated', {
          detail: this.part,
          bubbles: true,
          composed: true
        }));
      } else {
        this.errorMessage = result.error || 'Failed to update part';
        // Revert changes on error
        this.part = { ...this.originalPart };
      }
    } catch (error: any) {
      console.error('Error updating part:', error);
      this.errorMessage = error.message || 'Failed to update part';
      // Revert changes on error
      this.part = { ...this.originalPart };
    } finally {
      this.isLoading = false;
    }
  }

  private handleCancel() {
    this.part = { ...this.originalPart };
    this.isEditing = false;
    this.errorMessage = '';
  }

  private handleEdit() {
    this.originalPart = { ...this.part };
    this.isEditing = true;
    this.errorMessage = '';
  }

  private closeModal() {
    this.dispatchEvent(new CustomEvent('modal-closed', {
      bubbles: true,
      composed: true
    }));
  }

  private getStockStatusBadge(part: any) {
    if (part.stock_qty <= 0) {
      return html`<sl-badge variant="danger">Out of Stock</sl-badge>`;
    } else if (part.stock_qty <= part.reorder_level) {
      return html`<sl-badge variant="warning">Low Stock</sl-badge>`;
    } else {
      return html`<sl-badge variant="success">In Stock</sl-badge>`;
    }
  }

  private formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  render() {
    if (!this.part) {
      return html``;
    }

    const partName = this.part.name;
    const stockStatus = this.getStockStatusBadge(this.part);

    return html`
      <sl-dialog
        label="Part Viewer"
        .open=${this.open}
        @sl-hide=${this.closeModal}
      >
        <div class="part-header">
          <h2>${partName}</h2>
          <div class="part-status-badge">
            ${stockStatus}
          </div>
        </div>

        ${this.errorMessage ? html`
          <div class="error-message">
            <sl-icon name="exclamation-triangle" style="margin-right: 0.5rem;"></sl-icon>
            ${this.errorMessage}
          </div>
        ` : ''}

        <div class="part-fields">

          <div class="field-row" @click=${(e: Event) => e.stopPropagation()}>
            <div class="field-label">Name:</div>
            <div class="field-value">
              <sl-input
                value=${this.part.name}
                @sl-input=${(e: Event) => this.handleFieldUpdate('name', (e.target as HTMLInputElement).value)}
                @click=${(e: Event) => e.stopPropagation()}
                size="large"
                ?disabled=${!this.isEditing}
                autocomplete="off"
              ></sl-input>
            </div>
          </div>

          <div class="field-row" @click=${(e: Event) => e.stopPropagation()}>
            <div class="field-label">Description:</div>
            <div class="field-value">
              <sl-input
                value=${this.part.description || ''}
                @sl-input=${(e: Event) => this.handleFieldUpdate('description', (e.target as HTMLInputElement).value)}
                @click=${(e: Event) => e.stopPropagation()}
                size="large"
                ?disabled=${!this.isEditing}
              ></sl-input>
            </div>
          </div>

          <div class="field-row" @click=${(e: Event) => e.stopPropagation()}>
            <div class="field-label">Stock Quantity:</div>
            <div class="field-value">
              <sl-input
                type="number"
                value=${this.part.stock_qty}
                @sl-input=${(e: Event) => this.handleFieldUpdate('stock_qty', (e.target as HTMLInputElement).value)}
                @click=${(e: Event) => e.stopPropagation()}
                size="large"
                ?disabled=${!this.isEditing}
                min="0"
              ></sl-input>
            </div>
          </div>

          <div class="field-row" @click=${(e: Event) => e.stopPropagation()}>
            <div class="field-label">Minimum Quantity:</div>
            <div class="field-value">
              <sl-input
                type="number"
                value=${this.part.min_qty}
                @sl-input=${(e: Event) => this.handleFieldUpdate('min_qty', (e.target as HTMLInputElement).value)}
                @click=${(e: Event) => e.stopPropagation()}
                size="large"
                ?disabled=${!this.isEditing}
                min="0"
              ></sl-input>
            </div>
          </div>

          <div class="field-row" @click=${(e: Event) => e.stopPropagation()}>
            <div class="field-label">Maximum Quantity:</div>
            <div class="field-value">
              <sl-input
                type="number"
                value=${this.part.max_qty}
                @sl-input=${(e: Event) => this.handleFieldUpdate('max_qty', (e.target as HTMLInputElement).value)}
                @click=${(e: Event) => e.stopPropagation()}
                size="large"
                ?disabled=${!this.isEditing}
                min="0"
              ></sl-input>
            </div>
          </div>

          <div class="field-row" @click=${(e: Event) => e.stopPropagation()}>
            <div class="field-label">Reorder Level:</div>
            <div class="field-value">
              <sl-input
                type="number"
                value=${this.part.reorder_level}
                @sl-input=${(e: Event) => this.handleFieldUpdate('reorder_level', (e.target as HTMLInputElement).value)}
                @click=${(e: Event) => e.stopPropagation()}
                size="large"
                ?disabled=${!this.isEditing}
                min="0"
              ></sl-input>
            </div>
          </div>

          <div class="field-row" @click=${(e: Event) => e.stopPropagation()}>
            <div class="field-label">Cost:</div>
            <div class="field-value">
              <sl-input
                type="number"
                value=${this.part.cost}
                @sl-input=${(e: Event) => this.handleFieldUpdate('cost', (e.target as HTMLInputElement).value)}
                @click=${(e: Event) => e.stopPropagation()}
                size="large"
                ?disabled=${!this.isEditing}
                min="0"
                step="0.01"
              ></sl-input>
            </div>
          </div>

        </div>

        <div class="dialog-footer" slot="footer">
          ${this.isEditing ? html`
            <sl-button
              variant="neutral"
              size="large"
              @click=${this.handleCancel}
              ?disabled=${this.isLoading}
            >
              Cancel
            </sl-button>
            <sl-button
              variant="primary"
              size="large"
              @click=${this.handleSave}
              ?loading=${this.isLoading}
              ?disabled=${this.isLoading}
            >
              ${this.isLoading ? 'Saving...' : 'Save'}
            </sl-button>
          ` : html`
            <sl-button
              variant="neutral"
              size="large"
              @click=${this.closeModal}
            >
              Close
            </sl-button>
            <sl-button
              variant="primary"
              size="large"
              @click=${this.handleEdit}
            >
              Edit
            </sl-button>
          `}
        </div>
      </sl-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'part-viewer': PartViewer;
  }
}
